import * as React from 'react'
import { useSearchParams } from 'react-router-dom'

import { SortByShape, isSortByShape, useSortData } from '../../hooks/use-sort-data'
import { SortableValue } from '../../types'

export enum SortDirection {
  Ascending = 1,
  Descending = -1,
}

export interface TableSort<ColumnType> {
  column: ColumnType
  direction: SortDirection
}

/**
 * Default sort getter which either gets column matching by `defaultSortId` or returns the first column
 */
export function getDefaultSort<ColumnType, DefaultSort>(
  columns: ColumnType[],
  defaultSort?: DefaultSort
): TableSort<ColumnType> {
  if (defaultSort && typeof defaultSort === 'object') return defaultSort as unknown as TableSort<ColumnType>

  const foundColumn = columns.find((column) => isSortByShape(column) && column.id === defaultSort)

  return {
    column: foundColumn ?? (defaultSort as unknown as ColumnType | undefined) ?? columns[0],
    direction: SortDirection.Ascending,
  }
}

/**
 * Get default sorting from url when persisting sort
 */
export function getDefaultSortFromUrl<ColumnType extends SortByShape<any, any>>(
  columns: ColumnType[],
  order?: string | null,
  idKey: keyof ColumnType = 'id'
) {
  const isDesc = order?.startsWith('-')
  const queryParam = isDesc ? order?.substring(1) : order
  const column = columns.find((column) => column[idKey] === queryParam)

  if (column) {
    return { column, direction: isDesc ? SortDirection.Descending : SortDirection.Ascending }
  } else {
    return { column: columns[0], direction: SortDirection.Ascending }
  }
}

export function saveSortToUrl<ColumnType extends SortByShape<any, any>>(
  prev: URLSearchParams,
  urlPersistKey: string,
  newSort: ColumnType,
  idKey: keyof ColumnType = 'id',
  defaultSort: unknown = null
) {
  const newParams = new URLSearchParams(prev)
  const previousOrder = newParams.get(urlPersistKey) ?? defaultSort
  const isDescOrder = previousOrder?.toString().startsWith('-')
  newParams.set(urlPersistKey, `${!isDescOrder && previousOrder === newSort[idKey] ? '-' : ''}${newSort[idKey]}`)

  return newParams
}

export function useTableSort<
  IdType,
  DataType,
  ColumnType extends SortByShape<IdType, any>,
  DefaultSort extends ColumnType['id'] | TableSort<ColumnType>
>({
  data,
  columns,
  defaultSort,
  valueGetter,
  urlPersistKey,
  idKey = 'id',
}: {
  data: DataType[]
  columns: ColumnType[]

  /**
   * When set, the sorting will be persisted in the URL. Loading the
   *
   * @default undefined
   */
  urlPersistKey?: string

  /**
   * Key to get value of the URL sort from a column
   * @deafult "id"
   */
  idKey?: keyof ColumnType

  /**
   * You can either provide id of the column or whole shape
   * defaultSort: { column: columns[0], direction: -1 }
   * If your ids are constants (or enums), you will get name suggestions
   */
  defaultSort?: DefaultSort

  /**
   * Custom value getter. By default these can be defined on every column using `getValueForSort`
   */
  valueGetter?: (column: ColumnType) => (row: DataType) => SortableValue
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const defaultSortRef = React.useRef(
    urlPersistKey && typeof defaultSort === 'string'
      ? getDefaultSortFromUrl(columns, searchParams.get(urlPersistKey) ?? defaultSort, idKey)
      : getDefaultSort(columns, defaultSort)
  )
  const [sort, setSort] = React.useState(defaultSortRef.current)

  const changeSort = React.useCallback(
    (newSort: ColumnType) => {
      if (urlPersistKey) {
        setSearchParams(
          (prev) => saveSortToUrl(prev, urlPersistKey, newSort, idKey, defaultSortRef.current.column[idKey]),
          { preventScrollReset: true }
        )
      }

      setSort((prevSort) => {
        if (prevSort.column.id === newSort.id) {
          return { ...prevSort, direction: prevSort.direction * -1 } as TableSort<ColumnType>
        }

        return { column: newSort, direction: SortDirection.Ascending }
      })
    },
    [idKey, setSearchParams, urlPersistKey]
  )

  /**
   * Update sort from url
   */
  React.useEffect(() => {
    if (!urlPersistKey) return

    const newSort = searchParams.get(urlPersistKey)
    if (newSort) {
      const sort = getDefaultSortFromUrl(columns, newSort, idKey)
      setSort(sort)
    }
  }, [columns, idKey, searchParams, urlPersistKey])

  const sortedData = useSortData({ data, direction: sort.direction, sortBy: sort.column, valueGetter })

  return {
    data: sortedData,
    sort,
    changeSort,
  }
}
